import { supabase } from '@/integrations/supabase/client';

const BUCKET = 'p2b';

/**
 * Carica un'immagine di copertina per un itinerario su Supabase Storage.
 * Path: itineraries/{itineraryId}/wallpaper/{filename}
 * Restituisce il public URL completo pronto all'uso.
 */
export async function uploadItineraryWallpaper(
  itineraryId: string,
  file: File
): Promise<string> {
  const ext = file.name.split('.').pop() || 'jpg';
  const path = `itineraries/${itineraryId}/wallpaper/cover.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type });

  if (error) throw new Error(`Storage upload failed: ${error.message}`);

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  // Append cache-buster so the browser always fetches the newly upserted file
  return `${data.publicUrl}?t=${Date.now()}`;
}

/** Cartella storage delle immagini di un utente: qui vivono anche logo.png e logo_small. */
const userFolder = (userId: string) => `users/${userId}`;

/**
 * Carica la foto profilo dell'utente.
 * Path: users/{userId}/avatar.{ext}
 *
 * `upsert` da solo non basta a sovrascrivere: cambiando formato (avatar.png ->
 * avatar.jpg) il vecchio file resterebbe nel bucket per sempre, invisibile e non
 * piu' referenziato. Per questo dopo l'upload cancelliamo gli avatar con
 * estensione diversa.
 */
export async function uploadUserAvatar(userId: string, file: File): Promise<string> {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
  const path = `${userFolder(userId)}/avatar.${ext}`;

  const { error } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, { upsert: true, contentType: file.type });

  if (error) throw new Error(`Storage upload failed: ${error.message}`);

  await removeAvatarFiles(userId, path);

  const { data } = supabase.storage.from(BUCKET).getPublicUrl(path);
  // Cache-buster: senza, dopo la sostituzione il browser continua a mostrare la
  // vecchia foto, che sta allo stesso URL.
  return `${data.publicUrl}?t=${Date.now()}`;
}

/** Rimuove la foto profilo dal bucket, qualunque estensione abbia. */
export async function deleteUserAvatar(userId: string): Promise<void> {
  await removeAvatarFiles(userId);
}

/**
 * Cancella i file `avatar.*` dell'utente, saltando `keepPath` se indicato.
 *
 * Non solleva: un orfano rimasto nel bucket non deve far fallire un salvataggio
 * gia' andato a buon fine lato database.
 */
async function removeAvatarFiles(userId: string, keepPath?: string): Promise<void> {
  const folder = userFolder(userId);
  const { data, error } = await supabase.storage.from(BUCKET).list(folder);
  if (error || !data) return;

  const stale = data
    .filter((item) => item.name.startsWith('avatar.'))
    .map((item) => `${folder}/${item.name}`)
    .filter((path) => path !== keepPath);

  if (stale.length > 0) await supabase.storage.from(BUCKET).remove(stale);
}

/**
 * Data una Supabase public URL, restituisce la variante ridimensionata
 * tramite l'Image Transform API (/render/image/public/).
 * Funziona per qualsiasi immagine nel bucket p2b.
 * Se l'URL non è di Supabase Storage (es. base64 legacy), restituisce l'originale.
 */
export function getStorageThumbnailUrl(
  url: string | null | undefined,
  options: { width?: number; height?: number; quality?: number; resize?: 'cover' | 'contain' | 'fill'; noTransform?: boolean } = {}
): string | null {
  if (!url) return null;
  if (!url.includes('/storage/v1/object/public/')) return url; // base64 o URL esterna
  if (options.noTransform) return url; // skip transform se non disponibile

  const { width = 400, quality = 70, resize = 'cover' } = options;
  const transformUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  const params = new URLSearchParams();
  if (width)   params.set('width', String(width));
  if (options.height) params.set('height', String(options.height));
  params.set('quality', String(quality));
  params.set('resize', resize);

  return `${transformUrl}?${params.toString()}`;
}

/**
 * Converte un base64 data URL in un File object.
 * Utile per lo script di migrazione o futuri usi.
 */
export function dataUrlToFile(dataUrl: string, filename: string): File {
  const [header, base64] = dataUrl.split(',');
  const mime = header.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mime });
}

/**
 * Controlla se un valore del campo image è base64 (e quindi deve essere migrato).
 */
export function isBase64Image(image: string | null): boolean {
  return !!image && image.startsWith('data:image');
}
